"use client";

import { memo, useMemo } from "react";
import Link from "next/link";
import { Heading, type HeadingProps } from "@repo/ui/heading";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import MenuOverlay from "../menu-overlay/MenuOverlay";
import { useMenuOverlay } from "../menu-overlay/useMenuOverlay";

export type PreIntroHeading = {
  id: number;
  title: string;
  level: HeadingProps["level"];
};

export interface PreIntroProps {
  heading: PreIntroHeading;
  description?: BlocksContent | null;
  className?: string;
}

function PreIntroBase({ heading, description, className = "" }: PreIntroProps) {
  const hasDescription = useMemo(
    () => Array.isArray(description) && description.length > 0,
    [description],
  );

  // Overlay menu only (no reveal animations)
  const { open, toggleMenu, closeMenu } = useMenuOverlay(false);

  return (
    <>
      <section
        className={`max-w-7xl reveal-stagger mx-auto relative pt-18 pb-12 w-full ${className}`}
        aria-labelledby={`preintro-${heading.id}`}
        role="region"
      >
        {/* Left side "Menu" (no animation) */}
        <button
          onClick={toggleMenu}
          aria-controls="preintro-menu"
          aria-expanded={open}
          aria-label="Open menu"
          className="absolute cursor-pointer -left-20 inset-y-1/2 left-rotate-text font-normal xl:block hidden"
        >
          Menu
        </button>

        <div className="flex flex-col xl:gap-10 md:gap-8 gap-6 max-w-4xl">
          <Heading
            id={`preintro-${heading.id}`}
            level={heading.level || "h1"}
            className="xl:text-7xl lg:text-6xl md:text-5xl text-4xl leading-tight font-noto uppercase max-w-4xl reveal"
          >
            {/* No per-letter spans — plain text */}
            {heading.title}
          </Heading>

          {hasDescription && (
            <div className="xl:text-4xl md:text-3xl text-2xl max-w-4xl leading-tight tracking-tight text-grey-900 reveal-item">
              <BlocksRenderer content={description!} />
            </div>
          )}
        </div>

        {/* Right side "Contact" (no animation) */}
        <Link
          href="/contact/"
          className="absolute -right-20 inset-y-1/2 right-rotate-text font-normal xl:block hidden"
          aria-label="contact"
        >
          Contact
        </Link>
      </section>

      {/* Overlay menu (unchanged behavior) */}
      <MenuOverlay open={open} onClose={closeMenu} />
    </>
  );
}

export default memo(PreIntroBase);
